import { Component, type OnInit, DestroyRef, inject } from '@angular/core';
import { tap, switchMap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { UserRole } from '@protctc/common/core/models/user/user-role';
import { type NavigationLink } from '@protctc/common/core/models/navigation-link';
import { filterNull } from '@protctc/common/core/rxjs/filter-null';
import { NavigationService } from '@protctc/common/core/services/navigation.service';
import { UserService } from '@protctc/common/core/services/user.service';
import { CompaniesService } from '@protctc/common/core/services/companies.service';
import { routePaths } from '@protctc/common/core/utils/route-paths';
import { Company } from '@protctc/common/core/models/company/company';

import { SidebarComponent } from '../../sidebar/sidebar.component';

const USER_ROLE_SIDEBAR_LINKS_MAP = {
	[UserRole.BusinessOwner]: (data: { company: Company; }): NavigationLink[] => [
		{
			text: 'Dashboard',
			route: routePaths.dashboard,
			icon: 'dashboard',
		},
		{
			text: 'Outstanding invoices',
			route: routePaths.invoices,
			icon: 'invoices',
			disabled: !Company.isVerified(data.company),
		},
		{
			text: 'Estimates',
			route: routePaths.estimates,
			icon: 'file',
			disabled: !Company.isVerified(data.company),
		},
		{
			text: 'Schedule',
			route: routePaths.schedule,
			icon: 'schedule',
		},
		{
			text: 'Billing and Payments',
			route: routePaths.billingAndPayment,
			icon: 'billing',
			disabled: !Company.isVerified(data.company),
		},
		{
			text: 'Products & Services',
			route: routePaths.lineItems,
			icon: 'products',
		},
		{
			text: 'Users',
			route: routePaths.users,
			icon: 'users',
		},
		{
			text: 'Customers',
			route: routePaths.customers,
			icon: 'customers',
		},
		{
			text: 'Invoice setup',
			route: routePaths.invoiceSettings,
			icon: 'settings',
		},
	],
	[UserRole.SuperAdmin]: (): NavigationLink[] => [
		{
			text: 'Dashboard',
			route: routePaths.adminDashboard,
			icon: 'dashboard',
		},
		{
			text: 'Applications',
			route: routePaths.adminApplications,
			icon: 'application',
			children: [
				{
					text: 'Pending review',
					route: routePaths.adminPendingReviewApplications,
				},
				{
					text: 'Approved',
					route: routePaths.adminApprovedApplications,
				},
				{
					text: 'Rejected',
					route: routePaths.adminRejectedApplications,
				},
			],
		},
		{
			text: 'Merchant partners',
			route: routePaths.merchantPartners,
			icon: 'merchant',
			children: [
				{
					text: 'Active',
					route: routePaths.adminMerchantActive,
				},
				{
					text: 'Inactive',
					route: routePaths.adminMerchantInactive,
				},
			],
		},
		{
			text: 'Invoices',
			route: routePaths.adminInvoices,
			icon: 'invoices',
		},
		{
			text: 'Invoice settings',
			route: routePaths.adminInvoiceSettings,
			icon: 'settings',
		},
		{
			text: 'Users',
			route: routePaths.adminUsers,
			icon: 'users',
		},
	],
	[UserRole.FieldUser]: (): NavigationLink[] => [],
} satisfies Record<UserRole, (...args: never[]) => NavigationLink[]>;

/** Base layout component. */
@Component({
	selector: 'protctw-main-layout',
	templateUrl: './main-layout.component.html',
	styleUrls: ['./main-layout.component.css'],
	imports: [SidebarComponent, CommonModule],
})
export class MainLayoutComponent implements OnInit {
	private readonly userService = inject(UserService);

	private readonly navigationService = inject(NavigationService);

	private readonly companiesService = inject(CompaniesService);

	private readonly destroyRef = inject(DestroyRef);

	/** List of sidebar navigation links. */
	protected readonly sidebarNavigationLinks$ = this.navigationService.sidebarNavigationLinks$;

	/** @inheritdoc */
	public ngOnInit(): void {
		this.userService.currentUser$
			.pipe(
				filterNull(),
				switchMap(user => {
					if (user.role === UserRole.BusinessOwner) {
						// For business owners, we need company data to determine link states
						return this.companiesService.getCurrentCompany().pipe(
							tap(company => {
								const links = USER_ROLE_SIDEBAR_LINKS_MAP[UserRole.BusinessOwner]({ company });
								this.navigationService.setSidebarNavigationLinks(links);
							}),
						);
					}

					// For other roles, generate links without additional parameters
					const links = USER_ROLE_SIDEBAR_LINKS_MAP[user.role]();
					this.navigationService.setSidebarNavigationLinks(links);

					return [];
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe();
	}
}
